import React, { useCallback, useEffect, useState } from 'react';
import { CONTENT_TOP } from 'dashboard/layouts/admin/chromeGeometry';
import {
  Box,
  Chip,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useOutletContext } from 'react-router';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import {
  EmptyState,
  GlassButton,
  LoadingOverlay,
  PageHeader,
  PagePaper,
} from 'dashboard/components/common';

/**
 * Messages sent from contact sections on the brand's site.
 *
 * Enquiries are stored before they are emailed, precisely so a mail failure
 * does not lose a customer — but stored where nobody can read them is not
 * much better than lost. This is where they can be read.
 *
 * Undelivered ones are flagged rather than hidden. "We have your message but
 * could not forward it" is something the owner needs to see, because the fix
 * is theirs: a sending address that was never set up, or a key that stopped
 * working.
 */

const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  return d.toLocaleDateString();
};

export default function Enquiries() {
  const [selectedBrand] = useOutletContext();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!selectedBrand) return;
    setLoading(true);
    API.listEnquiries({ brand_id: selectedBrand })
      .then((res) => setRows(Array.isArray(res) ? res : []))
      .catch((err) => toast.error(err?.message || 'Could not load enquiries'))
      .finally(() => setLoading(false));
  }, [selectedBrand]);

  useEffect(() => { load(); }, [load]);

  const undelivered = rows.filter((r) => r.delivered === false).length;

  return (
    <Box sx={{ pt: CONTENT_TOP }}>
      <PagePaper>
        <PageHeader
          title='Enquiries'
          subtitle='Messages sent from the contact sections on your site. Kept here whether or not the email went through.'
          actions={
            <Stack direction='row' spacing='8px' alignItems='center' flexWrap='wrap'>
              {undelivered > 0 && (
                <Tooltip title='Stored here, but we could not email them on'>
                  <Chip
                    size='small'
                    color='warning'
                    icon={<WarningAmberIcon fontSize='small' />}
                    label={`${undelivered} not emailed`}
                  />
                </Tooltip>
              )}
              <GlassButton size='small' onClick={load}>Refresh</GlassButton>
            </Stack>
          }
        />

        {!selectedBrand && <EmptyState>Select a brand to see its enquiries.</EmptyState>}

        {selectedBrand && (
          <LoadingOverlay loading={loading && rows.length === 0} minHeight={220}>
            {!loading && rows.length === 0 && (
              <EmptyState>
                Nothing yet. Messages from a contact section on your site arrive here.
              </EmptyState>
            )}

            <Stack spacing='10px'>
              {rows.map((r) => (
                <Box
                  key={r._id}
                  sx={{
                    border: '1px solid', borderColor: 'divider',
                    borderRadius: '12px', p: '14px',
                  }}
                >
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    gap='10px'
                    flexWrap='wrap'
                    mb='8px'
                  >
                    <Stack direction='row' alignItems='center' spacing='8px' flexWrap='wrap'>
                      <Typography fontWeight='700' variant='body2'>{r.name}</Typography>
                      <Typography variant='caption' color='text.secondary'>{r.email}</Typography>
                      {r.delivered === false && (
                        <Chip size='small' color='warning' variant='outlined' label='Not emailed' />
                      )}
                    </Stack>
                    <Typography variant='caption' color='text.secondary'>
                      {when(r.created_at)}
                    </Typography>
                  </Stack>

                  <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', mb: '10px' }}>
                    {r.message}
                  </Typography>

                  <Divider sx={{ mb: '10px' }} />

                  <Stack direction='row' spacing='8px' alignItems='center' flexWrap='wrap'>
                    {/* mailto rather than a reply box: the brand's own mail
                        client has their signature, their history with this
                        person, and somewhere to file the reply. */}
                    <GlassButton
                      size='small'
                      onClick={() => {
                        window.location.href =
                          `mailto:${r.email}?subject=${encodeURIComponent(`Re: ${r.subject || 'your enquiry'}`)}`;
                      }}
                    >
                      Reply
                    </GlassButton>
                    {r.page_slug && (
                      <Typography variant='caption' color='text.secondary'>
                        sent from /{r.page_slug}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </LoadingOverlay>
        )}
      </PagePaper>
    </Box>
  );
}
